import React, { useState, useEffect, useCallback } from 'react';
import { Check, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { API_BASE } from '../config/apiBase';

const API = API_BASE;

const CATEGORY_ICONS = {
  'tecnologia': 'laptop', 'moda': 'shirt', 'casa-moveis': 'home',
  'beleza-cuidado': 'sparkles', 'esportes-fitness': 'dumbbell',
  'eletrodomesticos': 'zap', 'supermercado': 'shopping-cart',
  'bebes': 'baby', 'brinquedos': 'puzzle', 'ferramentas': 'wrench',
  'pet-shop': 'paw-print', 'saude': 'heart', 'construcao': 'hammer',
  'acessorios-veiculos': 'car', 'veiculos': 'truck',
  'imoveis': 'building-2', 'industria-comercio': 'factory',
  'para-seu-negocio': 'briefcase',
};

const EMOJI_MAP = {
  'tecnologia': '\uD83D\uDCBB', 'moda': '\uD83D\uDC57', 'casa-moveis': '\uD83C\uDFE0',
  'beleza-cuidado': '\u2728', 'esportes-fitness': '\uD83C\uDFCB\uFE0F',
  'eletrodomesticos': '\uD83C\uDF1F', 'supermercado': '\uD83D\uDED2',
  'bebes': '\uD83D\uDC76', 'brinquedos': '\uD83E\uDDE9', 'ferramentas': '\uD83D\uDD27',
  'pet-shop': '\uD83D\uDC3E', 'saude': '\u2764\uFE0F', 'construcao': '\uD83D\uDD28',
  'acessorios-veiculos': '\uD83D\uDE97', 'veiculos': '\uD83D\uDE9A',
  'imoveis': '\uD83C\uDFE2', 'industria-comercio': '\uD83C\uDFED',
  'para-seu-negocio': '\uD83D\uDCBC',
};

function fetchAuth(url, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(url, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) } });
}

export default function CategoryOnboardingModal({ onComplete }) {
  const [step, setStep] = useState(1); // 1 = categories, 2 = subcategories
  const [categories, setCategories] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAuth(`${API}/api/categories/full`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setCategories(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const toggleCat = (slug) => {
    setSelectedCats(prev => {
      if (prev.includes(slug)) return prev.filter(s => s !== slug);
      if (prev.length >= 5) return prev;
      return [...prev, slug];
    });
  };

  const toggleSub = (sub) => {
    setSelectedSubs(prev =>
      prev.includes(sub) ? prev.filter(s => s !== sub) : [...prev, sub]
    );
  };

  const goToStep2 = useCallback(() => {
    if (selectedCats.length === 0) return;
    const subsMap = {};
    for (const cat of categories) {
      if (selectedCats.includes(cat.slug)) {
        subsMap[cat.slug] = cat.subcategories || [];
      }
    }
    setSubcategories(subsMap);
    setStep(2);
  }, [selectedCats, categories]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetchAuth(`${API}/api/empresa/category-preferences`, {
        method: 'POST',
        body: JSON.stringify({ categories: selectedCats, subcategories: selectedSubs }),
      });
    } catch (e) { console.error(e); }
    setSaving(false);
    onComplete?.();
  };

  if (loading) return null;

  return (
    <div className="cat-onboard-overlay" data-testid="category-onboarding-modal">
      <div className="cat-onboard-modal">
        {/* Header */}
        <div className="cat-onboard-header">
          <div className="cat-onboard-icon-wrap">
            <Sparkles size={22} />
          </div>
          <h2 className="cat-onboard-title" data-testid="cat-onboard-title">
            {step === 1 ? 'Escolha seus nichos' : 'Refine suas categorias'}
          </h2>
          <p className="cat-onboard-desc">
            {step === 1
              ? 'Selecione ate 5 categorias para receber ofertas personalizadas desses nichos.'
              : 'Selecione as subcategorias que mais interessam ao seu negocio.'}
          </p>
          <div className="cat-onboard-steps">
            <span className={`cat-onboard-step-dot ${step >= 1 ? 'active' : ''}`} />
            <span className={`cat-onboard-step-dot ${step >= 2 ? 'active' : ''}`} />
          </div>
        </div>

        {/* Body */}
        <div className="cat-onboard-body">
          {step === 1 && (
            <div className="cat-onboard-grid" data-testid="cat-onboard-categories">
              {categories.map(cat => {
                const selected = selectedCats.includes(cat.slug);
                return (
                  <button
                    key={cat.slug}
                    data-testid={`cat-btn-${cat.slug}`}
                    className={`cat-onboard-card ${selected ? 'selected' : ''}`}
                    onClick={() => toggleCat(cat.slug)}
                  >
                    <span className="cat-onboard-card-emoji">{EMOJI_MAP[cat.slug] || '\uD83D\uDCE6'}</span>
                    <span className="cat-onboard-card-name">{cat.name}</span>
                    {selected && <span className="cat-onboard-check"><Check size={14} /></span>}
                  </button>
                );
              })}
            </div>
          )}

          {step === 2 && (
            <div className="cat-onboard-subs" data-testid="cat-onboard-subcategories">
              {Object.entries(subcategories).map(([slug, subs]) => {
                const cat = categories.find(c => c.slug === slug);
                return (
                  <div key={slug} className="cat-onboard-sub-group">
                    <h3 className="cat-onboard-sub-title">
                      {EMOJI_MAP[slug] || ''} {cat?.name || slug}
                    </h3>
                    <div className="cat-onboard-sub-chips">
                      {subs.map(sub => {
                        const sel = selectedSubs.includes(`${slug}:${sub}`);
                        return (
                          <button
                            key={sub}
                            data-testid={`sub-btn-${slug}-${sub.replace(/\s/g, '-')}`}
                            className={`cat-onboard-chip ${sel ? 'selected' : ''}`}
                            onClick={() => toggleSub(`${slug}:${sub}`)}
                          >
                            {sub}
                            {sel && <Check size={11} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="cat-onboard-footer">
          {step === 2 && (
            <button className="cat-onboard-btn-back" data-testid="cat-onboard-back" onClick={() => setStep(1)}>
              <ChevronLeft size={14} /> Voltar
            </button>
          )}
          <div className="cat-onboard-footer-info">
            {step === 1 && selectedCats.length > 0 && (
              <span className="cat-onboard-count">{selectedCats.length}/5 selecionada(s)</span>
            )}
            {step === 2 && selectedSubs.length > 0 && (
              <span className="cat-onboard-count">{selectedSubs.length} subcategoria(s)</span>
            )}
          </div>
          {step === 1 ? (
            <button
              className="cat-onboard-btn-next"
              data-testid="cat-onboard-next"
              onClick={goToStep2}
              disabled={selectedCats.length === 0}
            >
              Continuar <ChevronRight size={14} />
            </button>
          ) : (
            <button
              className="cat-onboard-btn-save"
              data-testid="cat-onboard-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Salvando...' : 'Confirmar e comecar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
